import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Autocomplete, Input, Select } from 'common-src/components/base';
import { getRoleConfig } from 'common-src/features/auth';
import { apiRequest, HttpMethods } from 'common-src/features/rest';
import { getActiveClients, getClientOptions } from 'common-src/models/Client';
import { isFeatureEnabled } from 'common-src/models/FeatureFlag';
import { getPatient, PlatformStatus } from 'common-src/models/Patient';
import {
  getActivePatientCaregivers,
  getPatientCaregiversRequest,
} from 'common-src/models/PatientCaregiver';
import PatientOncehubBooking, {
  OncehubBookingStatuses,
  updateOncehubBooking,
  VisitType,
} from 'common-src/models/PatientOncehubBooking';
import { ColorsNew } from 'common-src/styles';

import { InfoItem } from 'src/components/elements';
import { TimeItem } from 'src/components/table';
import { getCallStatus } from 'src/fragments/ScheduledCallsFragment/ScheduledCallsList/helpers';
import useCustomSelector from 'src/hooks/useCustomSelector';
import usePopup from 'src/hooks/usePopup';
import BasePopup from 'src/popups/BasePopup';

import styles from './EditVisitPopup.module.scss';

const statusOptions = [
  OncehubBookingStatuses.missed,
  OncehubBookingStatuses.completed,
  OncehubBookingStatuses.canceled,
];

const EditVisitPopup = ({ onClose, timeZone, scheduledCall, caregiversOnly = false }) => {
  const dispatch = useDispatch();

  // For Athena visits we currently only support editing caregivers (attendees).
  const isCaregiversOnly = caregiversOnly || !!scheduledCall?.isAthenaAppointment;

  const [data, setData] = useState({});
  const [initialCaregiverIds, setInitialCaregiverIds] = useState([]);
  const [initialCaregiversById, setInitialCaregiversById] = useState({});
  // Per-caregiver attendee data
  const [attendeeById, setAttendeeById] = useState({}); // { [caregiverId]: { confirmed, presence_mode, remote_link } }
  const [initialAttendeeById, setInitialAttendeeById] = useState({});
  const [attendeeIdByCaregiverId, setAttendeeIdByCaregiverId] = useState({}); // { [caregiverId]: attendeeUUID }

  // Athena visit UUID (DB id in athena_appointment_visits)
  const athenaVisitId = useMemo(() => {
    if (!scheduledCall?.isAthenaAppointment) return null;
    return scheduledCall?.athenaData?.id || scheduledCall?._fields?.id || scheduledCall?.id || null;
  }, [scheduledCall]);

  const clients = useCustomSelector((state) => getActiveClients(state));
  const permissions =
    useCustomSelector((state) => getRoleConfig(state)?.patientChart?.sections?.scheduledCalls) ||
    false;

  const { renderButtons } = usePopup();

  const clientOptions = useMemo(() => getClientOptions(clients, true), [clients]);

  // Derive patientId from either pre-filled data or selected patient
  const currentPatientId = useMemo(
    () => data.patient?.id || data.patientId,
    [data.patient?.id, data.patientId],
  );

  // Read patient from store when we have an id
  const currentPatient = useCustomSelector((state) =>
    currentPatientId ? getPatient(state, currentPatientId) : null,
  );

  // Feature flag: caregivers workflow toggle
  const isCdrCaregiverWFEnabled = useCustomSelector((state) =>
    isFeatureEnabled(state, 'cdrCaregiverWF'),
  );

  // Show caregivers only for Cognitive Active or GUIDE patients
  const isCognitiveOrGuide = useMemo(() => {
    const p = currentPatient || data.patient || {};
    return Boolean(p?.isGuide || p?.cognitiveStatus === PlatformStatus.Active);
  }, [currentPatient, data.patient]);

  // Fetch caregivers when patient is known
  useEffect(() => {
    if (!currentPatientId) return;
    dispatch(getPatientCaregiversRequest({ patientId: currentPatientId }));
  }, [currentPatientId]);

  // Active caregivers for the patient (from store)
  const caregivers = useCustomSelector((state) =>
    currentPatientId ? getActivePatientCaregivers(state, currentPatientId) : [],
  );

  const caregiverOptions = useMemo(
    () =>
      caregivers.map((c) => {
        const name = `${c.firstName} ${c.lastName}`.trim();
        const label = c?.relationship ? `${name}, ${c.relationship}` : name;
        return { value: c.id, label };
      }),
    [caregivers],
  );

  // Ensure attached caregivers (possibly inactive) also appear in the dropdown/value
  const extendedCaregiverOptions = useMemo(() => {
    const base = caregiverOptions || [];
    const baseIds = new Set(base.map((o) => o.value));
    const missing = (initialCaregiverIds || [])
      .filter((id) => !baseIds.has(id))
      .map((id) => ({ value: id, label: initialCaregiversById[id] || String(id) }));
    return [...base, ...missing];
  }, [caregiverOptions, initialCaregiverIds, initialCaregiversById]);

  useEffect(() => {
    if (_.isEmpty(scheduledCall)) return;
    // For athenaAppointment, read from _fields, otherwise read from the object directly
    if (scheduledCall.isAthenaAppointment) {
      setData({
        clientId: scheduledCall._fields?.clientId || scheduledCall.clientId,
        status: scheduledCall._fields?.status,
        visitReason: scheduledCall._fields?.visitReason,
        visitType: scheduledCall._fields?.visitType,
        patientId: scheduledCall._fields?.patientId || scheduledCall.patientId,
      });
    } else {
      setData(
        _.pick(scheduledCall, ['clientId', 'status', 'visitReason', 'visitType', 'patientId']),
      );
    }
  }, []);

  // Prefill already attached caregivers (attendees) for this appointment
  useEffect(() => {
    if (!scheduledCall?.id) return;
    if (scheduledCall?.isAthenaAppointment) return;
    (async () => {
      try {
        const res = await apiRequest({
          endpoint: `appointments/${scheduledCall.id}`,
          method: HttpMethods.Get,
        });
        if (!res.ok) return; // silently ignore if not available
        const raw = await res.json();
        const appointment = raw?.PatientOncehubBooking
          ? Array.isArray(raw.PatientOncehubBooking)
            ? raw.PatientOncehubBooking[0]
            : raw.PatientOncehubBooking
          : raw;
        const attendees = Array.isArray(appointment?.attendees) ? appointment.attendees : [];
        const ids = attendees
          .map((a) => a?.patient_caregiver_id || a?.patientCaregiverId)
          .filter((x) => typeof x === 'string' && x.length > 0);
        const byId = attendees.reduce((acc, a) => {
          const id = a?.patient_caregiver_id || a?.patientCaregiverId;
          const pcg = a?.patient_caregiver || a?.patientCaregiver;
          if (id && pcg) {
            const first = pcg?.firstName ?? pcg?.first_name;
            const last = pcg?.lastName ?? pcg?.last_name;
            const baseName = [first, last].filter(Boolean).join(' ').trim();
            const rel = pcg?.relationship;
            const label = rel ? `${baseName}, ${rel}` : baseName;
            if (baseName) acc[id] = label;
          }
          return acc;
        }, {});
        const attendeeData = attendees.reduce((acc, a) => {
          const id = a?.patient_caregiver_id || a?.patientCaregiverId;
          if (id) {
            acc[id] = {
              confirmed: !!a?.confirmed,
              presence_mode: a?.presence_mode ?? a?.presenceMode ?? null,
              remote_link: a?.remote_link ?? a?.remoteLink ?? '',
            };
          }
          return acc;
        }, {});
        const attendeeIdsMap = attendees.reduce((acc, a) => {
          const cgId = a?.patient_caregiver_id || a?.patientCaregiverId;
          const attendeeId = a?.id;
          if (cgId && attendeeId) acc[cgId] = attendeeId;
          return acc;
        }, {});
        setInitialCaregiverIds(ids);
        setInitialCaregiversById(byId);
        setInitialAttendeeById(attendeeData);
        setAttendeeById(attendeeData);
        setAttendeeIdByCaregiverId(attendeeIdsMap);
        setData((prev) => ({ ...prev, caregiverId: ids?.[0] }));
      } catch (_) {
        // ignore
      }
    })();
  }, [scheduledCall?.id]);

  // Prefill already attached caregivers (attendees) for Athena visit
  useEffect(() => {
    if (!scheduledCall?.isAthenaAppointment) return;
    if (!athenaVisitId) return;

    (async () => {
      try {
        const res = await apiRequest({
          endpoint: `athena/appointments/visits/${athenaVisitId}/attendees`,
          method: HttpMethods.Get,
        });
        if (!res.ok) return;
        const raw = await res.json();
        const attendees = Array.isArray(raw?.attendees)
          ? raw.attendees
          : Array.isArray(raw)
            ? raw
            : [];

        const ids = attendees
          .map((a) => a?.patient_caregiver_id || a?.patientCaregiverId)
          .filter((x) => typeof x === 'string' && x.length > 0);

        const attendeeData = attendees.reduce((acc, a) => {
          const id = a?.patient_caregiver_id || a?.patientCaregiverId;
          if (id) {
            acc[id] = {
              confirmed: !!a?.confirmed,
              presence_mode: a?.presence_mode ?? a?.presenceMode ?? null,
              remote_link: a?.remote_link ?? a?.remoteLink ?? '',
            };
          }
          return acc;
        }, {});

        const attendeeIdsMap = attendees.reduce((acc, a) => {
          const cgId = a?.patient_caregiver_id || a?.patientCaregiverId;
          const attendeeId = a?.id;
          if (cgId && attendeeId) acc[cgId] = attendeeId;
          return acc;
        }, {});

        setInitialCaregiverIds(ids);
        setInitialAttendeeById(attendeeData);
        setAttendeeById(attendeeData);
        setAttendeeIdByCaregiverId(attendeeIdsMap);
        setData((prev) => ({ ...prev, caregiverId: ids?.[0] }));
      } catch (_) {
        // ignore
      }
    })();
  }, [scheduledCall?.isAthenaAppointment, athenaVisitId]);

  const onSubmitHandler = (loadingCallback, successCallback, errorCallback) => {
    loadingCallback();

    // In caregiversOnly mode we don't want to update the OnceHub booking resource itself.
    // We only want to create/update/delete attendees for the appointment.
    // This avoids calling `patientOncehubBookings/undefined` when the modal is opened
    // from an aggregated list item that doesn't carry the booking id.
    const shouldUpdateOncehubBooking = !isCaregiversOnly && !scheduledCall?.isAthenaAppointment;

    const payload = {
      clientId: data.clientId,
      status: data.status,
      visitReason: data.visitReason,
      visitType: data.visitType,
      patientId: data.patient ? data.patient.id : data.patientId,
    };

    const createAttendeesIfNeeded = async () => {
      const selectedId = data.caregiverId;
      const initialIdsSet = new Set(initialCaregiverIds || []);
      const newIds = selectedId && !initialIdsSet.has(selectedId) ? [selectedId] : [];
      if (!newIds.length) return;

      // OnceHub
      if (!scheduledCall?.isAthenaAppointment) {
        if (!scheduledCall?.id) return;
        const results = await Promise.all(
          newIds.map((id) =>
            apiRequest({
              endpoint: `appointments/${scheduledCall.id}/attendees`,
              method: HttpMethods.Post,
              body: {
                patient_caregiver_id: id,
                confirmed: !!attendeeById?.[id]?.confirmed,
                presence_mode: attendeeById?.[id]?.presence_mode ?? null,
                remote_link: attendeeById?.[id]?.remote_link || undefined,
              },
            }).then(async (res) => {
              if (!res.ok) {
                let msg = 'Failed to add attendee';
                let err;
                try {
                  err = await res.json();
                } catch (_) {
                  err = null;
                }
                msg = err?.detail || err?.user_message || msg;
                throw new Error(msg);
              }
              return res.json();
            }),
          ),
        );
        return results;
      }

      // Athena
      if (!athenaVisitId) return;
      const results = await Promise.all(
        newIds.map((id) =>
          apiRequest({
            endpoint: `athena/appointments/visits/${athenaVisitId}/attendees`,
            method: HttpMethods.Post,
            body: {
              patient_caregiver_id: id,
              confirmed: !!attendeeById?.[id]?.confirmed,
              presence_mode: attendeeById?.[id]?.presence_mode ?? null,
              remote_link: attendeeById?.[id]?.remote_link || undefined,
            },
          }).then(async (res) => {
            if (!res.ok) {
              let msg = 'Failed to add attendee';
              let err;
              try {
                err = await res.json();
              } catch (_) {
                err = null;
              }
              msg = err?.detail || err?.user_message || msg;
              throw new Error(msg);
            }
            return res.json();
          }),
        ),
      );

      return results;
    };

    const deleteAttendeesIfNeeded = async () => {
      const selectedId = data.caregiverId;
      const removedIds = (initialCaregiverIds || []).filter((id) => id !== selectedId);
      if (!removedIds.length) return;

      // OnceHub
      if (!scheduledCall?.isAthenaAppointment) {
        if (!scheduledCall?.id) return;
        await Promise.all(
          removedIds.map((id) => {
            const attendeeUuid = attendeeIdByCaregiverId?.[id];
            if (!attendeeUuid) return Promise.resolve();
            return apiRequest({
              endpoint: `appointments/attendees/${attendeeUuid}`,
              method: HttpMethods.Delete,
            }).then(async (res) => {
              if (!res.ok) {
                let msg = 'Failed to delete attendee';
                let err;
                try {
                  err = await res.json();
                } catch (_) {
                  err = null;
                }
                msg = err?.detail || err?.user_message || msg;
                throw new Error(msg);
              }
              return true;
            });
          }),
        );
        return;
      }

      // Athena
      if (!athenaVisitId) return;
      await Promise.all(
        removedIds.map((id) => {
          const attendeeUuid = attendeeIdByCaregiverId?.[id];
          if (!attendeeUuid) return Promise.resolve();
          return apiRequest({
            endpoint: `athena/appointments/visits/${athenaVisitId}/attendees/${attendeeUuid}`,
            method: HttpMethods.Delete,
          }).then(async (res) => {
            if (!res.ok) {
              let msg = 'Failed to delete attendee';
              let err;
              try {
                err = await res.json();
              } catch (_) {
                err = null;
              }
              msg = err?.detail || err?.user_message || msg;
              throw new Error(msg);
            }
            return true;
          });
        }),
      );
    };

    const updateAttendeesIfNeeded = async () => {
      const selectedId = data.caregiverId;
      const initialIdsSet = new Set(initialCaregiverIds || []);
      const commonIds = selectedId && initialIdsSet.has(selectedId) ? [selectedId] : [];
      const updates = [];
      commonIds.forEach((id) => {
        const before = initialAttendeeById?.[id] || {};
        const after = attendeeById?.[id] || {};
        const fields = ['confirmed', 'presence_mode', 'remote_link'];
        const changed = fields.some((k) => {
          const b = before?.[k];
          const a = after?.[k];
          return (b ?? null) !== (a ?? null);
        });
        if (changed) {
          const attendeeUuid = attendeeIdByCaregiverId?.[id];
          if (attendeeUuid) {
            updates.push(
              apiRequest({
                endpoint: scheduledCall?.isAthenaAppointment
                  ? `athena/appointments/visits/${athenaVisitId}/attendees/${attendeeUuid}`
                  : `appointments/attendees/${attendeeUuid}`,
                method: HttpMethods.Put,
                body: {
                  confirmed: !!after?.confirmed,
                  presence_mode: after?.presence_mode ?? null,
                  remote_link: after?.remote_link || undefined,
                },
              }).then(async (res) => {
                if (!res.ok) {
                  let msg = 'Failed to update attendee';
                  let err;
                  try {
                    err = await res.json();
                  } catch (_) {
                    // Swallow JSON parse/network errors and keep default message
                    err = null;
                  }
                  msg = err?.detail || err?.user_message || msg;
                  throw new Error(msg);
                }
                return res.json();
              }),
            );
          }
        }
      });
      if (!updates.length) return;
      await Promise.all(updates);
    };

    const afterSuccessfulUpdate = async () => {
      try {
        await deleteAttendeesIfNeeded();
        await createAttendeesIfNeeded();
        await updateAttendeesIfNeeded();

        // Refresh attendee state so the selection persists in UI without requiring a full page reload.
        // This works only for OnceHub appointments (Athena doesn't have these endpoints).
        if (!scheduledCall?.isAthenaAppointment && scheduledCall?.id) {
          try {
            const res = await apiRequest({
              endpoint: `appointments/${scheduledCall.id}`,
              method: HttpMethods.Get,
            });
            if (res.ok) {
              const raw = await res.json();
              const appointment = raw?.PatientOncehubBooking
                ? Array.isArray(raw.PatientOncehubBooking)
                  ? raw.PatientOncehubBooking[0]
                  : raw.PatientOncehubBooking
                : raw;
              const attendees = Array.isArray(appointment?.attendees) ? appointment.attendees : [];
              const ids = attendees
                .map((a) => a?.patient_caregiver_id || a?.patientCaregiverId)
                .filter((x) => typeof x === 'string' && x.length > 0);
              const byId = attendees.reduce((acc, a) => {
                const id = a?.patient_caregiver_id || a?.patientCaregiverId;
                const pcg = a?.patient_caregiver || a?.patientCaregiver;
                if (id && pcg) {
                  const first = pcg?.firstName ?? pcg?.first_name;
                  const last = pcg?.lastName ?? pcg?.last_name;
                  const baseName = [first, last].filter(Boolean).join(' ').trim();
                  const rel = pcg?.relationship;
                  const label = rel ? `${baseName}, ${rel}` : baseName;
                  if (baseName) acc[id] = label;
                }
                return acc;
              }, {});
              const attendeeData = attendees.reduce((acc, a) => {
                const id = a?.patient_caregiver_id || a?.patientCaregiverId;
                if (id) {
                  acc[id] = {
                    confirmed: !!a?.confirmed,
                    presence_mode: a?.presence_mode ?? a?.presenceMode ?? null,
                    remote_link: a?.remote_link ?? a?.remoteLink ?? '',
                  };
                }
                return acc;
              }, {});
              const attendeeIdsMap = attendees.reduce((acc, a) => {
                const cgId = a?.patient_caregiver_id || a?.patientCaregiverId;
                const attendeeId = a?.id;
                if (cgId && attendeeId) acc[cgId] = attendeeId;
                return acc;
              }, {});
              setInitialCaregiverIds(ids);
              setInitialCaregiversById(byId);
              setInitialAttendeeById(attendeeData);
              setAttendeeById(attendeeData);
              setAttendeeIdByCaregiverId(attendeeIdsMap);
            }
          } catch (_) {
            // ignore
          }
        }

        successCallback('Visit edited!');
        onClose(true);
      } catch (e) {
        errorCallback(e.message || e);
      }
    };

    if (!shouldUpdateOncehubBooking) {
      // caregiversOnly mode (or Athena visit). Skip updating the visit record; just sync attendees.
      // But we still need an appointment id to create/update/delete attendees.
      if (scheduledCall?.isAthenaAppointment) {
        if (!athenaVisitId) {
          errorCallback('Missing Athena visit id; unable to save caregiver.');
          return;
        }
      } else if (!scheduledCall?.id) {
        errorCallback('Missing appointment id; unable to save caregiver.');
        return;
      }
      afterSuccessfulUpdate();
      return;
    }

    if (!scheduledCall?.id) {
      errorCallback('Missing visit id; unable to update visit.');
      return;
    }

    dispatch(
      updateOncehubBooking(scheduledCall.id, payload, {
        successBlock: afterSuccessfulUpdate,
        errorBlock: (err) => errorCallback(err),
      }),
    );
  };

  const getStatus = () => {
    const status = Object.values(OncehubBookingStatuses).find((op) => op.value === data.status);

    return status?.value === OncehubBookingStatuses.scheduled.value &&
      getCallStatus(scheduledCall, timeZone) === OncehubBookingStatuses.today.value
      ? OncehubBookingStatuses.today
      : status;
  };

  const renderStatus = () => {
    if (permissions?.canEditVisitStatus) {
      return (
        <Select
          id="status"
          label="Status"
          options={statusOptions}
          onChange={(op) => setData((prev) => ({ ...prev, status: op.value }))}
          value={getStatus()}
          withError={false}
          size="small"
          paperHeight={200}
        />
      );
    }

    return <InfoItem title="Status" content={getStatus()?.label} textId="status" isSmall />;
  };

  const renderAssigned = () => {
    if (permissions?.canEditVisitAssigned) {
      return (
        <Select
          id="assigned"
          label="Assigned"
          classNames={[styles.select]}
          options={clientOptions}
          isSearchable
          onChange={(op) => setData((prev) => ({ ...prev, clientId: op.value }))}
          value={clientOptions.find((op) => op.value === data.clientId)}
          withError={false}
          size="small"
          paperHeight={200}
        />
      );
    }

    return (
      <InfoItem
        title="Assigned"
        content={clientOptions.find((op) => op.value === data.clientId)?.label ?? ''}
        textId="assigned"
        isSmall
      />
    );
  };

  // NOTE: intentionally no debug logging here; this popup is user-facing.

  return (
    <BasePopup id="edit-visit" open onClose={onClose} title="Edit Visit">
      <div className={styles.grid}>
        {scheduledCall?.isAthenaAppointment && !isCaregiversOnly && (
          <InfoItem
            title="Athena Visit"
            content={
              [
                scheduledCall?.athenaData?.athenaAppointmentId
                  ? `Appointment ID: ${scheduledCall.athenaData.athenaAppointmentId}`
                  : null,
                scheduledCall?._fields?.athenaAppointmentId
                  ? `Appointment ID: ${scheduledCall._fields.athenaAppointmentId}`
                  : null,
                scheduledCall?.athenaData?.athenaPracticeId
                  ? `Practice: ${scheduledCall.athenaData.athenaPracticeId}`
                  : null,
              ]
                .filter(Boolean)
                .join(' | ') || '—'
            }
            textId="athena-visit-metadata"
            isSmall
          />
        )}
        {!isCaregiversOnly && (
          <InfoItem
            title="Scheduled Time"
            content={
              <TimeItem
                date={
                  scheduledCall.isAthenaAppointment
                    ? scheduledCall._fields?.scheduledAt
                    : scheduledCall.scheduledAt
                }
                duration={
                  scheduledCall.isAthenaAppointment
                    ? scheduledCall._fields?.durationMinutes
                    : scheduledCall.durationMinutes
                }
                timeZone={timeZone}
                id="scheduled-time"
              />
            }
            isSmall
          />
        )}
        {!isCaregiversOnly && (
          <InfoItem
            title="Created Time"
            content={
              <TimeItem
                date={
                  scheduledCall.isAthenaAppointment
                    ? scheduledCall._fields?.createdAt
                    : scheduledCall.createdAt
                }
                timeZone={timeZone}
                id="created-time"
              />
            }
            isSmall
          />
        )}
        {!isCaregiversOnly && (
          <Select
            id="type-select"
            label="Type"
            options={Object.values(VisitType)}
            onChange={(op) => setData((prev) => ({ ...prev, visitType: op.value }))}
            value={Object.values(VisitType).find((vt) => vt.value === data.visitType)}
            withError={false}
            size="small"
            paperHeight={200}
          />
        )}
        {!isCaregiversOnly && renderStatus()}
        <div className={styles.width100}>
          {!isCaregiversOnly && (
            <Input
              id="visit-reason"
              classNames={[styles.input]}
              label="Visit Reason"
              value={data.visitReason || ''}
              onTextChange={(text) => setData((prev) => ({ ...prev, visitReason: text }))}
              size="small"
            />
          )}
          {!isCaregiversOnly && renderAssigned()}
          {!isCaregiversOnly && !data.patientId && (
            <>
              <Autocomplete
                id="search-patients"
                classNames={[styles.input, 'm-t-16']}
                label="Assign patient"
                initialOptionsCount={20}
                renderOption={(patient) => (
                  <span className={[styles.option, 'font-w-500', 'font-s-14'].join(' ')}>
                    MRN {patient.id}, {patient.firstName} {patient.lastName}
                  </span>
                )}
                onOptionSelect={(patient) => setData((prev) => ({ ...prev, patient }))}
                paperStyles={{
                  width: 'max-content',
                  maxHeight: '150px',
                  color: ColorsNew.mediumDarkGreen,
                }}
                getRequest={(searchText, count) =>
                  apiRequest({
                    endpoint: `members/search/${searchText}`,
                    queryParams: { skip: 0, limit: count },
                  })
                }
                isSmall
              />
              <InfoItem
                id="patient"
                textId="patient"
                classNames={[styles.width100, 'm-t-16']}
                title="Patient"
                content={
                  data.patient
                    ? `MRN ${data.patient?.id}, ${data.patient?.firstName} ${data.patient?.lastName}`
                    : ''
                }
                multiline
                isSmall
              />
            </>
          )}

          {/* Caregiver section (only for Cognitive or GUIDE patients) and feature-flag gated */}
          {(isCaregiversOnly || (isCognitiveOrGuide && isCdrCaregiverWFEnabled)) && (
            <>
              {/* Divider before Caregiver section */}
              <div className={styles.sectionSeparator} />

              <div className={[styles.caregiverSection, styles.input, 'm-t-20'].join(' ')}>
                <div
                  className={['flex-row', 'vertically-centered', styles.caregiverControlsRow].join(
                    ' ',
                  )}
                >
                  <p
                    className={[styles.caregiverTitle, 'font-w-600'].join(' ')}
                    style={{ color: ColorsNew.mediumDarkGreen }}
                  >
                    Caregivers (Attendees)
                  </p>
                </div>
                {currentPatientId ? (
                  <Select
                    id="caregiver-select"
                    label="Select caregiver to add"
                    classNames={[styles.select]}
                    options={extendedCaregiverOptions}
                    isSearchable
                    isClearable
                    customFilter={(op, text) => op.label.toLowerCase().includes(text.toLowerCase())}
                    onChange={(op) => setData((prev) => ({ ...prev, caregiverId: op?.value }))}
                    value={
                      extendedCaregiverOptions.find((op) => op.value === data.caregiverId) || null
                    }
                    withError={false}
                    size="small"
                    paperHeight={300}
                    placeholder={extendedCaregiverOptions.length ? 'Select' : 'No caregivers found'}
                    isDisabled={!extendedCaregiverOptions.length}
                  />
                ) : (
                  <InfoItem
                    title="Caregiver"
                    content="Assign a patient to select a caregiver"
                    textId="caregiver-info"
                    isSmall
                  />
                )}
                {/* Per-caregiver attendee fields (single caregiver) */}
                {data.caregiverId && (
                  <div className={[styles.width100, 'm-t-12', 'flex-column', 'gap-12'].join(' ')}>
                    {(() => {
                      const id = data.caregiverId;
                      const current = attendeeById?.[id] || {};
                      const name =
                        caregiverOptions.find((op) => op.value === id)?.label ||
                        initialCaregiversById[id] ||
                        id;
                      return (
                        <div
                          key={`cg-${id}`}
                          className={[
                            styles.caregiverCard,
                            'flex-column',
                            'gap-8',
                            'secondary-border',
                            'border-r-6',
                          ].join(' ')}
                        >
                          <p
                            className={[styles.caregiverName, 'font-s-12', 'font-w-600'].join(' ')}
                            style={{ color: ColorsNew.mediumDarkGreen }}
                          >
                            {name}
                          </p>
                          <div className={['flex-row', 'gap-12', 'wrap'].join(' ')}>
                            <Select
                              id={`attendee-status-${id}`}
                              label="Status"
                              options={[
                                { label: 'Pending', value: false },
                                { label: 'Confirmed', value: true },
                              ]}
                              onChange={(op) =>
                                setAttendeeById((prev) => ({
                                  ...prev,
                                  [id]: { ...prev[id], confirmed: Boolean(op?.value) },
                                }))
                              }
                              value={[
                                { label: 'Pending', value: false },
                                { label: 'Confirmed', value: true },
                              ].find((op) => op.value === !!current.confirmed)}
                              withError={false}
                              size="small"
                              paperHeight={200}
                            />
                            <Select
                              id={`attendee-presence-${id}`}
                              label="Presence"
                              options={[
                                { label: 'Not set', value: null },
                                { label: 'Remote', value: 'remote' },
                                { label: 'In-person', value: 'in_person' },
                              ]}
                              onChange={(op) =>
                                setAttendeeById((prev) => ({
                                  ...prev,
                                  [id]: { ...prev[id], presence_mode: op?.value ?? null },
                                }))
                              }
                              value={
                                [
                                  { label: 'Not set', value: null },
                                  { label: 'Remote', value: 'remote' },
                                  { label: 'In-person', value: 'in_person' },
                                ].find((op) => op.value === (current.presence_mode ?? null)) || {
                                  label: 'Not set',
                                  value: null,
                                }
                              }
                              withError={false}
                              size="small"
                              paperHeight={200}
                              paperStyles={{ minWidth: '200px' }}
                            />
                            {current.presence_mode === 'remote' && (
                              <Input
                                id={`attendee-remote-link-${id}`}
                                label="Remote link"
                                value={current.remote_link || ''}
                                onTextChange={(text) =>
                                  setAttendeeById((prev) => ({
                                    ...prev,
                                    [id]: { ...prev[id], remote_link: text },
                                  }))
                                }
                                size="small"
                              />
                            )}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}
                {data.caregiverId && (
                  <div
                    className={[
                      styles.caregiverInfoText,
                      'm-t-8',
                      'flex-row',
                      'gap-8',
                      'wrap',
                    ].join(' ')}
                    style={{ color: ColorsNew.mediumDarkGreen }}
                  >
                    {(() => {
                      const currentId = data.caregiverId;
                      const initialIds = [...new Set(initialCaregiverIds)];
                      const isSame = initialIds.length === 1 && initialIds[0] === currentId;
                      const prefix = isSame ? 'Attached' : 'Selected';
                      return [
                        <span key="prefix" className="font-s-12 font-w-500">
                          {prefix}:
                        </span>,
                        (() => {
                          const id = currentId;
                          const label =
                            caregiverOptions.find((op) => op.value === id)?.label ||
                            initialCaregiversById[id] ||
                            id;
                          return (
                            <span
                              key={`chip-${id}`}
                              className={[
                                styles.caregiverChip,
                                'flex-row',
                                'vertically-centered',
                                'gap-6',
                              ].join(' ')}
                            >
                              <span className="font-s-12 font-w-500">{label}</span>
                              <button
                                type="button"
                                className={styles.caregiverChipRemove}
                                aria-label={`Remove ${label}`}
                                onClick={() =>
                                  setData((prev) => ({ ...prev, caregiverId: undefined }))
                                }
                              >
                                ×
                              </button>
                            </span>
                          );
                        })(),
                      ];
                    })()}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
      {(() => {
        // baseline visit field changes
        const baselineBefore = scheduledCall.isAthenaAppointment
          ? {
              clientId: scheduledCall._fields?.clientId || scheduledCall.clientId,
              status: scheduledCall._fields?.status,
              visitReason: scheduledCall._fields?.visitReason,
              visitType: scheduledCall._fields?.visitType,
            }
          : _.pick(scheduledCall, ['clientId', 'status', 'visitReason', 'visitType']);
        const baselineAfter = _.pick(data, ['clientId', 'status', 'visitReason', 'visitType']);
        const baseChanged = !_.isEqual(baselineBefore, baselineAfter);

        // caregiver selection change (single)
        const selectedId = data.caregiverId;
        const caregiverSelectionChanged = (() => {
          const had = initialCaregiverIds || [];
          if (had.length === 0) {
            // no previous caregiver -> changed only if we now selected one
            return !(selectedId === undefined || selectedId === null);
          }
          if (had.length === 1) {
            return had[0] !== selectedId;
          }
          // previously had multiple attached -> any single-selection state is a change
          return true;
        })();

        // per-caregiver field changes only if same caregiver remains attached
        const initialIdsSet = new Set(initialCaregiverIds || []);
        const hasFieldChanges =
          selectedId && initialIdsSet.has(selectedId)
            ? (() => {
                const before = initialAttendeeById?.[selectedId] || {};
                const after = attendeeById?.[selectedId] || {};
                const fields = ['confirmed', 'presence_mode', 'remote_link'];
                return fields.some((k) => {
                  const b = before?.[k];
                  const a = after?.[k];
                  return (b ?? null) !== (a ?? null);
                });
              })()
            : false;

        return renderButtons({
          containerClassName: styles.buttonsContainer,
          onClose,
          onSubmit: onSubmitHandler,
          isSubmitEnabled: baseChanged || caregiverSelectionChanged || hasFieldChanges,
          submitButtonText: 'Save and Close',
        });
      })()}
    </BasePopup>
  );
};

EditVisitPopup.propTypes = {
  onClose: PropTypes.func,
  timeZone: PropTypes.string,
  scheduledCall: PropTypes.exact(PatientOncehubBooking.schema),
  caregiversOnly: PropTypes.bool,
};

export default EditVisitPopup;
